import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { uploadImage, convertImageToBase64, deleteImage } from '../utils/imageUpload';
import { showSuccess, showError, showWarning } from '../utils/toast';

const QuestionLibrary = ({ onAddToExam, exam, currentQuestions }) => {
  const [libraryQuestions, setLibraryQuestions] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [selectedSection, setSelectedSection] = useState(0);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterDifficulty, setFilterDifficulty] = useState('all');
  const [filterMarks, setFilterMarks] = useState('all');
  const [filterSubject, setFilterSubject] = useState('all');
  const [availableSubjects, setAvailableSubjects] = useState([]);

  const [questionForm, setQuestionForm] = useState({
    questionText: '',
    questionImage: '',
    questionType: 'mcq',
    marks: '',
    difficultyLevel: 'medium',
    subjectName: '',
    options: [
      { text: '', image: '', isCorrect: false },
      { text: '', image: '', isCorrect: false }
    ],
    correctAnswer: ''
  });

  useEffect(() => {
    fetchLibraryQuestions();
  }, []);

  const fetchLibraryQuestions = async () => {
    try {
      const adminId = localStorage.getItem('adminId');
      const q = query(collection(db, 'questionLibrary'), where('organizationId', '==', adminId));
      const querySnapshot = await getDocs(q);
      const questionsList = [];
      querySnapshot.forEach((doc) => {
        questionsList.push({ id: doc.id, ...doc.data() });
      });
      setLibraryQuestions(questionsList);
      
      // Extract unique subjects
      extractAvailableSubjects(questionsList);
    } catch (error) {
      console.error('Error fetching library questions:', error);
      showError('Failed to load question library');
    }
  };

  const extractAvailableSubjects = (questionsList) => {
    // Extract unique non-empty subject names
    const subjects = [...new Set(
      questionsList
        .map(q => q.subjectName)
        .filter(subject => subject && subject.trim() !== '')
    )].sort();
    setAvailableSubjects(subjects);
  };

  const handleSelectQuestion = (questionId) => {
    setSelectedQuestions(prev => {
      if (prev.includes(questionId)) {
        return prev.filter(id => id !== questionId);
      } else {
        return [...prev, questionId];
      }
    });
  };

  const handleSelectAll = () => {
    const filtered = getFilteredQuestions();
    if (selectedQuestions.length === filtered.length) {
      setSelectedQuestions([]);
    } else {
      setSelectedQuestions(filtered.map(q => q.id));
    }
  };

  const handleAddSelectedToExam = () => {
    if (selectedQuestions.length === 0) {
      showWarning('Please select at least one question');
      return;
    }
    const questions = libraryQuestions.filter(q => selectedQuestions.includes(q.id));
    onAddToExam(questions, selectedSection);
    setSelectedQuestions([]);
    showSuccess(`${questions.length} question(s) added to exam`);
  };

  const handleEditQuestion = (question) => {
    setEditingQuestion(question);
    setQuestionForm({
      questionText: question.questionText,
      questionImage: question.questionImage || '',
      questionType: question.questionType,
      marks: question.marks,
      difficultyLevel: question.difficultyLevel,
      subjectName: question.subjectName || '',
      options: question.options || [{ text: '', image: '', isCorrect: false }, { text: '', image: '', isCorrect: false }],
      correctAnswer: question.correctAnswer || ''
    });
    setShowEditModal(true);
  };

  const handleUpdateQuestion = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!questionForm.questionText && !questionForm.questionImage) {
        showError('Please provide either question text or image');
        setLoading(false);
        return;
      }

      if (!questionForm.marks || questionForm.marks <= 0) {
        showError('Please enter valid marks');
        setLoading(false);
        return;
      }

      if (questionForm.questionType === 'mcq') {
        const validOptions = questionForm.options.filter(opt => opt.text || opt.image);
        if (validOptions.length < 2) {
          showError('MCQ must have at least 2 options');
          setLoading(false);
          return;
        }

        const correctOptions = questionForm.options.filter(opt => opt.isCorrect);
        if (correctOptions.length === 0) {
          showError('Please mark at least one correct answer');
          setLoading(false);
          return;
        }
      }

      const questionData = {
        questionText: questionForm.questionText,
        questionImage: questionForm.questionImage,
        questionType: questionForm.questionType,
        marks: parseFloat(questionForm.marks),
        difficultyLevel: questionForm.difficultyLevel,
        subjectName: questionForm.subjectName || '',
        updatedAt: new Date().toISOString()
      };

      if (questionForm.questionType === 'mcq') {
        questionData.options = questionForm.options.filter(opt => opt.text || opt.image);
      } else {
        questionData.correctAnswer = questionForm.correctAnswer;
      }

      await updateDoc(doc(db, 'questionLibrary', editingQuestion.id), questionData);
      
      showSuccess('Question updated successfully!');
      setShowEditModal(false);
      setEditingQuestion(null);
      fetchLibraryQuestions();
    } catch (error) {
      console.error('Error updating question:', error);
      showError('Failed to update question');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm('Are you sure you want to delete this question from library?')) return;

    try {
      const questionToDelete = libraryQuestions.find(q => q.id === questionId);
      
      if (questionToDelete?.questionImage) {
        await deleteImage(questionToDelete.questionImage);
      }
      
      if (questionToDelete?.options && questionToDelete.options.length > 0) {
        for (const option of questionToDelete.options) {
          if (option.image) {
            await deleteImage(option.image);
          }
        }
      }
      
      await deleteDoc(doc(db, 'questionLibrary', questionId));
      showSuccess('Question deleted from library!');
      fetchLibraryQuestions();
    } catch (error) {
      console.error('Error deleting question:', error);
      showError('Failed to delete question');
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setQuestionForm({
      ...questionForm,
      [name]: value
    });
  };

  const handleOptionChange = (index, field, value) => {
    const newOptions = [...questionForm.options];
    newOptions[index][field] = value;
    setQuestionForm({
      ...questionForm,
      options: newOptions
    });
  };

  const handleQuestionImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      showError('Image size must be less than 5MB');
      return;
    }

    if (!file.type.startsWith('image/')) {
      showError('Please select a valid image file');
      return;
    }

    setUploadingImage(true);
    const result = await uploadImage(file, 'oesweb', 'questions');
    
    if (result.success) {
      setQuestionForm({ ...questionForm, questionImage: result.url });
      setUploadingImage(false);
      showSuccess('Image uploaded successfully!');
    } else {
      try {
        const base64Image = await convertImageToBase64(file);
        setQuestionForm({ ...questionForm, questionImage: base64Image });
        setUploadingImage(false);
        showSuccess('Image loaded successfully!');
      } catch (base64Error) {
        setUploadingImage(false);
        showError('Image upload failed: ' + result.error);
      }
    }
  };

  const handleOptionImageUpload = async (index, e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      showError('Image size must be less than 5MB');
      return;
    }

    if (!file.type.startsWith('image/')) {
      showError('Please select a valid image file');
      return;
    }

    setUploadingImage(true);
    const result = await uploadImage(file, 'oesweb', 'options');
    
    if (result.success) {
      const newOptions = [...questionForm.options];
      newOptions[index].image = result.url;
      setQuestionForm({ ...questionForm, options: newOptions });
      setUploadingImage(false);
      showSuccess('Option image uploaded successfully!');
    } else {
      try {
        const base64Image = await convertImageToBase64(file);
        const newOptions = [...questionForm.options];
        newOptions[index].image = base64Image;
        setQuestionForm({ ...questionForm, options: newOptions });
        setUploadingImage(false);
        showSuccess('Image loaded successfully!');
      } catch (base64Error) {
        setUploadingImage(false);
        showError('Image upload failed: ' + result.error);
      }
    }
  };

  const addOption = () => {
    setQuestionForm({
      ...questionForm,
      options: [...questionForm.options, { text: '', image: '', isCorrect: false }]
    });
  };

  const removeOption = (index) => {
    const newOptions = questionForm.options.filter((_, i) => i !== index);
    setQuestionForm({
      ...questionForm,
      options: newOptions
    });
  };

  const getFilteredQuestions = () => {
    return libraryQuestions.filter(q => {
      const matchesSearch = (q.questionText && q.questionText.toLowerCase().includes(searchTerm.toLowerCase())) || 
                           (q.subjectName && q.subjectName.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesType = filterType === 'all' || q.questionType === filterType;
      const matchesDifficulty = filterDifficulty === 'all' || q.difficultyLevel === filterDifficulty;
      const matchesMarks = filterMarks === 'all' || q.marks === parseFloat(filterMarks);
      const matchesSubject = filterSubject === 'all' || 
                            (filterSubject === 'none' && (!q.subjectName || q.subjectName.trim() === '')) ||
                            q.subjectName === filterSubject;
      return matchesSearch && matchesType && matchesDifficulty && matchesMarks && matchesSubject;
    });
  };

  const filteredQuestions = getFilteredQuestions();

  return (
    <div className="space-y-6">
      {/* Header with Search and Filters */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <h3 className="text-2xl font-bold text-gray-900 mb-4">Question Library</h3>
        
        <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-4 mb-4">
          <input
            type="text"
            placeholder="Search questions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
          />
          
          <select
            value={filterSubject}
            onChange={(e) => setFilterSubject(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
          >
            <option value="all">All Subjects</option>
            <option value="none">No Subject</option>
            {availableSubjects.map((subject) => (
              <option key={subject} value={subject}>
                {subject}
              </option>
            ))}
          </select>
          
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
          >
            <option value="all">All Types</option>
            <option value="mcq">MCQ</option>
            <option value="short">Short Answer</option>
            <option value="long">Long Answer</option>
          </select>
          
          <select
            value={filterDifficulty}
            onChange={(e) => setFilterDifficulty(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
          >
            <option value="all">All Difficulties</option>
            <option value="easy">Easy</option>
            <option value="medium">Medium</option>
            <option value="hard">Hard</option>
          </select>

          <select
            value={filterMarks}
            onChange={(e) => setFilterMarks(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
          >
            <option value="all">All Marks</option>
            <option value="1">1 Mark</option>
            <option value="2">2 Marks</option>
            <option value="3">3 Marks</option>
            <option value="4">4 Marks</option>
            <option value="5">5 Marks</option>
          </select>

          <button
            onClick={handleSelectAll}
            className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-semibold hover:bg-gray-300 transition"
          >
            {selectedQuestions.length === filteredQuestions.length && filteredQuestions.length > 0 ? 'Deselect All' : 'Select All'}
          </button>
        </div>

        {/* Section Selector - Always Visible */}
        {exam?.sections && exam.sections.length > 1 && (
          <div className="mt-4 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
            <div className="flex items-center gap-3 flex-wrap">
              <label className="text-sm font-semibold text-gray-900">Add to Section:</label>
              <select
                value={selectedSection}
                onChange={(e) => setSelectedSection(parseInt(e.target.value))}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 font-semibold"
              >
                {exam.sections.map((section, index) => {
                  const sectionQuestions = currentQuestions?.filter(q => q.sectionId === index) || [];
                  const expectedCount = parseInt(section.questions) || 0;
                  const actualCount = sectionQuestions.length;
                  const isFull = actualCount >= expectedCount;
                  return (
                    <option key={index} value={index}>
                      {section.name || `Section ${index + 1}`} ({actualCount}/{expectedCount}) {isFull ? '✓ Full' : `- ${expectedCount - actualCount} needed`}
                    </option>
                  );
                })}
              </select>
              {(() => {
                const sectionQuestions = currentQuestions?.filter(q => q.sectionId === selectedSection) || [];
                const section = exam.sections[selectedSection];
                const expectedCount = parseInt(section?.questions) || 0;
                const actualCount = sectionQuestions.length;
                const isFull = actualCount >= expectedCount;
                const remaining = expectedCount - actualCount;
                
                if (isFull) {
                  return (
                    <div className="flex items-center gap-2 px-3 py-2 bg-green-100 border border-green-300 rounded-lg">
                      <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-sm font-semibold text-green-800">Section Complete ({actualCount}/{expectedCount})</span>
                    </div>
                  );
                } else if (remaining > 0) {
                  return (
                    <div className="flex items-center gap-2 px-3 py-2 bg-yellow-100 border border-yellow-300 rounded-lg">
                      <svg className="h-5 w-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-sm font-semibold text-yellow-800">Need {remaining} more question{remaining !== 1 ? 's' : ''}</span>
                    </div>
                  );
                }
              })()}
            </div>
          </div>
        )}

        {selectedQuestions.length > 0 && (
          <div className="flex items-center gap-3 flex-wrap mt-4">
            {(() => {
              // Check if selected section is full
              const sectionQuestions = currentQuestions?.filter(q => q.sectionId === selectedSection) || [];
              const section = exam?.sections?.[selectedSection];
              const expectedCount = parseInt(section?.questions) || 0;
              const actualCount = sectionQuestions.length;
              const isFull = actualCount >= expectedCount;
              const wouldExceed = actualCount + selectedQuestions.length > expectedCount;
              
              if (isFull) {
                return (
                  <div className="w-full p-4 bg-red-50 border border-red-300 rounded-lg">
                    <div className="flex items-start gap-3">
                      <svg className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <div>
                        <p className="text-sm font-bold text-red-800">Section is already full!</p>
                        <p className="text-sm text-red-700 mt-1">
                          "{section?.name || `Section ${selectedSection + 1}`}" has {actualCount}/{expectedCount} questions. 
                          Please select a different section or remove existing questions first.
                        </p>
                      </div>
                    </div>
                  </div>
                );
              } else if (wouldExceed) {
                return (
                  <div className="w-full p-4 bg-orange-50 border border-orange-300 rounded-lg">
                    <div className="flex items-start gap-3">
                      <svg className="h-6 w-6 text-orange-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <div>
                        <p className="text-sm font-bold text-orange-800">Too many questions selected!</p>
                        <p className="text-sm text-orange-700 mt-1">
                          "{section?.name || `Section ${selectedSection + 1}`}" has {actualCount}/{expectedCount} questions. 
                          You're trying to add {selectedQuestions.length} more, which exceeds the limit by {actualCount + selectedQuestions.length - expectedCount}.
                          Please reduce selection or choose another section.
                        </p>
                      </div>
                    </div>
                  </div>
                );
              }
            })()}
            
            <button
              onClick={handleAddSelectedToExam}
              disabled={(() => {
                const sectionQuestions = currentQuestions?.filter(q => q.sectionId === selectedSection) || [];
                const section = exam?.sections?.[selectedSection];
                const expectedCount = parseInt(section?.questions) || 0;
                const actualCount = sectionQuestions.length;
                return actualCount >= expectedCount || (actualCount + selectedQuestions.length > expectedCount);
              })()}
              className={`px-6 py-2 rounded-lg font-semibold transition ${
                (() => {
                  const sectionQuestions = currentQuestions?.filter(q => q.sectionId === selectedSection) || [];
                  const section = exam?.sections?.[selectedSection];
                  const expectedCount = parseInt(section?.questions) || 0;
                  const actualCount = sectionQuestions.length;
                  const isDisabled = actualCount >= expectedCount || (actualCount + selectedQuestions.length > expectedCount);
                  
                  return isDisabled
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:shadow-lg';
                })()
              }`}
            >
              Add {selectedQuestions.length} to Exam
            </button>
            <button
              onClick={() => setSelectedQuestions([])}
              className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg font-semibold hover:bg-gray-300 transition"
            >
              Clear Selection
            </button>
          </div>
        )}
      </div>

      {/* Questions List */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h4 className="text-lg font-bold text-gray-900">Library Questions ({filteredQuestions.length})</h4>
        </div>
        
        {filteredQuestions.length === 0 ? (
          <div className="text-center py-12">
            <svg className="h-16 w-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-gray-600 font-medium">No questions in library</p>
            <p className="text-gray-500 text-sm mt-2">Add questions to library for reuse across exams</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredQuestions.map((question, index) => (
              <div key={question.id} className={`p-6 hover:bg-gray-50 transition ${selectedQuestions.includes(question.id) ? 'bg-indigo-50' : ''}`}>
                <div className="flex items-start gap-4">
                  <input
                    type="checkbox"
                    checked={selectedQuestions.includes(question.id)}
                    onChange={() => handleSelectQuestion(question.id)}
                    className="mt-1 w-5 h-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  />
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-xs font-semibold">
                        Q{index + 1}
                      </span>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        question.questionType === 'mcq' ? 'bg-blue-100 text-blue-800' :
                        question.questionType === 'short' ? 'bg-green-100 text-green-800' :
                        'bg-purple-100 text-purple-800'
                      }`}>
                        {question.questionType === 'mcq' ? 'MCQ' : 
                         question.questionType === 'short' ? 'Short Answer' : 'Long Answer'}
                      </span>
                      {question.difficultyLevel && (
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          question.difficultyLevel === 'easy' ? 'bg-green-100 text-green-800' :
                          question.difficultyLevel === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {question.difficultyLevel.charAt(0).toUpperCase() + question.difficultyLevel.slice(1)}
                        </span>
                      )}
                      <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-semibold">
                        {question.marks} {question.marks === 1 ? 'Mark' : 'Marks'}
                      </span>
                      {question.subjectName && (
                        <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-xs font-semibold">
                          📚 {question.subjectName}
                        </span>
                      )}
                    </div>

                    {question.questionText && (
                      <p className="text-gray-900 font-medium mb-2">{question.questionText}</p>
                    )}
                    
                    {question.questionImage && (
                      <img src={question.questionImage} alt="Question" className="max-w-md rounded-lg border border-gray-200 mb-3" />
                    )}

                    {question.questionType === 'mcq' && question.options && (
                      <div className="mt-3 space-y-2">
                        {question.options.map((option, optIndex) => (
                          <div key={optIndex} className={`flex items-start gap-2 p-2 rounded ${option.isCorrect ? 'bg-green-50 border border-green-200' : 'bg-gray-50'}`}>
                            <span className="font-semibold text-gray-600">{String.fromCharCode(65 + optIndex)}.</span>
                            <div className="flex-1">
                              {option.text && <p className="text-gray-700">{option.text}</p>}
                              {option.image && <img src={option.image} alt={`Option ${optIndex + 1}`} className="max-w-xs rounded mt-1" />}
                            </div>
                            {option.isCorrect && (
                              <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEditQuestion(question)}
                      className="text-indigo-600 hover:text-indigo-800 p-2 hover:bg-indigo-50 rounded-lg transition"
                    >
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDeleteQuestion(question.id)}
                      className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded-lg transition"
                    >
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit Modal - Similar to ManageQuestions edit functionality */}
      {showEditModal && editingQuestion && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full my-8">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-8 py-4 flex items-center justify-between rounded-t-xl">
              <h4 className="text-xl font-bold text-gray-900">Edit Library Question</h4>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-gray-600 transition"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleUpdateQuestion} className="p-8 space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto">
              {/* Question Text */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Question Text</label>
                <textarea
                  value={questionForm.questionText}
                  onChange={(e) => setQuestionForm({ ...questionForm, questionText: e.target.value })}
                  rows="3"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                  placeholder="Enter your question here..."
                />
              </div>

              {/* Question Image Upload */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Question Image (Optional)</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleQuestionImageUpload}
                  disabled={uploadingImage}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                />
                {uploadingImage && <p className="text-sm text-indigo-600 mt-2">Uploading image...</p>}
                {questionForm.questionImage && (
                  <div className="mt-3">
                    <img src={questionForm.questionImage} alt="Question" className="max-w-xs rounded-lg border border-gray-200" />
                    <button
                      type="button"
                      onClick={async () => {
                        await deleteImage(questionForm.questionImage);
                        setQuestionForm({ ...questionForm, questionImage: '' });
                      }}
                      className="text-sm text-red-600 hover:text-red-800 mt-2"
                    >
                      Remove Image
                    </button>
                  </div>
                )}
              </div>

              {/* Subject/Concept Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Subject / Concept Name (Optional)</label>
                <input
                  type="text"
                  name="subjectName"
                  value={questionForm.subjectName}
                  onChange={handleFormChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                  placeholder="e.g., Physics - Mechanics, Math - Algebra, etc."
                />
                <p className="text-xs text-gray-500 mt-1">Helps categorize questions by subject or topic</p>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                {/* Question Type */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Question Type *</label>
                  <select
                    name="questionType"
                    value={questionForm.questionType}
                    onChange={handleFormChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                  >
                    <option value="mcq">Multiple Choice (MCQ)</option>
                    <option value="short">Short Answer</option>
                    <option value="long">Long Answer</option>
                  </select>
                </div>

                {/* Marks */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Marks *</label>
                  <input
                    type="number"
                    name="marks"
                    required
                    min="0.5"
                    step="0.5"
                    value={questionForm.marks}
                    onChange={handleFormChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                    placeholder="e.g., 2"
                  />
                </div>

                {/* Difficulty Level */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Difficulty Level *</label>
                  <select
                    name="difficultyLevel"
                    value={questionForm.difficultyLevel}
                    onChange={handleFormChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                  >
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                  </select>
                </div>
              </div>

              {/* MCQ Options */}
              {questionForm.questionType === 'mcq' && (
                <div className="border-t border-gray-200 pt-6">
                  <div className="flex justify-between items-center mb-4">
                    <h5 className="text-lg font-bold text-gray-900">Options</h5>
                    <button
                      type="button"
                      onClick={addOption}
                      className="bg-green-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-green-700 transition flex items-center gap-2"
                    >
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Add Option
                    </button>
                  </div>

                  <div className="space-y-4">
                    {questionForm.options.map((option, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                        <div className="flex justify-between items-center mb-3">
                          <span className="font-semibold text-gray-700">Option {index + 1}</span>
                          <div className="flex items-center gap-2">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={option.isCorrect}
                                onChange={(e) => handleOptionChange(index, 'isCorrect', e.target.checked)}
                                className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                              />
                              <span className="text-sm font-medium text-green-700">Correct Answer</span>
                            </label>
                            {questionForm.options.length > 2 && (
                              <button
                                type="button"
                                onClick={() => removeOption(index)}
                                className="text-red-600 hover:text-red-800 p-1"
                              >
                                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </div>

                        <div className="space-y-3">
                          <input
                            type="text"
                            value={option.text}
                            onChange={(e) => handleOptionChange(index, 'text', e.target.value)}
                            placeholder="Option text"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                          />

                          <div>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleOptionImageUpload(index, e)}
                              disabled={uploadingImage}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition text-sm"
                            />
                            {option.image && (
                              <div className="mt-2">
                                <img src={option.image} alt={`Option ${index + 1}`} className="max-w-xs rounded-lg border border-gray-200" />
                                <button
                                  type="button"
                                  onClick={async () => {
                                    await deleteImage(option.image);
                                    handleOptionChange(index, 'image', '');
                                  }}
                                  className="text-sm text-red-600 hover:text-red-800 mt-1"
                                >
                                  Remove Image
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Correct Answer for Short/Long Answer */}
              {(questionForm.questionType === 'short' || questionForm.questionType === 'long') && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Expected Answer / Keywords (Optional)
                  </label>
                  <textarea
                    name="correctAnswer"
                    value={questionForm.correctAnswer}
                    onChange={handleFormChange}
                    rows="3"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                    placeholder="Enter expected answer or keywords for reference..."
                  />
                </div>
              )}

              {/* Submit Button */}
              <div className="flex gap-4 pt-4 border-t border-gray-200">
                <button
                  type="submit"
                  disabled={loading || uploadingImage}
                  className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-3 rounded-lg font-semibold hover:shadow-lg transition disabled:opacity-50"
                >
                  {loading ? 'Updating...' : 'Update Question'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="bg-gray-200 text-gray-700 px-8 py-3 rounded-lg font-semibold hover:bg-gray-300 transition"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionLibrary;
